// Change-history extraction for the review list's per-use-case "Metric history" — turns a
// chronological series of daily Value_Snapshot__c mirror rows (one per account per UTC day,
// see api.ts's day-scoped External_ID__c upsert) into a changes-only log per use case.
//
// v3 keys: use case id (`uc-*`) -> key -> entries, where the key is either one of the use case's
// question keys (every input the SE can edit) or one of its leading-metric keys (the
// `Metric_Definition__c` key the observation is written against, e.g. `uc-crm-enrichment-accounts`,
// holding the today value). Only v3 stories (`v: 2`) are read: a v2-calculator snapshot's history
// is keyed on lever ids that no longer exist, so those rows are skipped.
//
// Pure and math-free: no DB/HTTP imports, so it stays unit-testable and safe to import from both
// api.ts (server) and wire.ts (the wire schema both api.ts and the frontend api-client use).

import { z } from 'zod';
import { safeParseJson } from '../../_shared/parse-json';
import { ValueStorySchema } from './value-story-schema';

export interface RawSnapshotRow {
  storyJson: string | null;
  createdDate: string | null;
}

const MetricHistoryEntrySchema = z.object({
  date: z.string(),
  value: z.union([z.number(), z.literal('')]),
  /** True when this entry reverted the metric back to its engine default (no customer value). */
  assumed: z.boolean(),
});
export type MetricHistoryEntry = z.infer<typeof MetricHistoryEntrySchema>;

/** useCaseId → input or metric key → entries, newest first. Only metrics recorded (non-assumed) at least
 *  once appear here — a metric that has only ever sat at its default is not "history", it's
 *  just a default the review list already shows as Assumed. */
export const MetricHistoryMapSchema = z.record(z.string(), z.record(z.string(), z.array(MetricHistoryEntrySchema)));
export type MetricHistoryMap = z.infer<typeof MetricHistoryMapSchema>;

interface ParsedPoint {
  date: string;
  useCaseId: string;
  key: string;
  value: number | '';
  assumed: boolean;
}

function parseSnapshotRow(row: RawSnapshotRow): ParsedPoint[] {
  if (!row.storyJson || !row.createdDate) return [];
  const parsed = safeParseJson(ValueStorySchema, row.storyJson);
  if (!parsed.success) return []; // a v2-calculator snapshot, or not a story at all
  const points: ParsedPoint[] = [];
  for (const uc of parsed.data.useCases) {
    for (const input of uc.inputs) {
      points.push({ date: row.createdDate, useCaseId: uc.id, key: input.key, value: input.value, assumed: input.assumed });
    }
    for (const m of uc.metrics) {
      points.push({ date: row.createdDate, useCaseId: uc.id, key: m.key, value: m.today, assumed: m.assumed });
    }
  }
  return points;
}

// U+0001 joins the use case id and the key into one map key — a control character can't collide
// with either half (both are plain ASCII identifiers like 'uc-tam-sourcing' / 'accounts_year_today').
const KEY_SEP = String.fromCharCode(1);

function sliceKey(useCaseId: string, key: string): string {
  return useCaseId + KEY_SEP + key;
}

function splitSliceKey(key: string): [useCaseId: string, key: string] {
  const i = key.indexOf(KEY_SEP);
  return [key.slice(0, i), key.slice(i + 1)];
}

/**
 * Builds a changes-only history per (use case id, key) from a series of daily snapshot
 * rows. `rows` must be ordered OLDEST FIRST (the caller's SQL query is `ORDER BY created_date
 * ASC`) — the collapse walks forward and emits an entry whenever a metric's (value, assumed)
 * pair differs from the previous one, with one deliberate exception: a metric's very first
 * appearance is never emitted while it's still assumed — that's just "hasn't been recorded
 * yet", not a change worth logging. Once a metric has been recorded at least once, every later
 * change DOES emit, including a revert back to assumed (shown in the tab as "reverted to
 * default"). A row with unparseable or missing storyJson/createdDate is skipped rather than
 * failing the whole build.
 */
export function buildMetricHistory(rows: RawSnapshotRow[]): MetricHistoryMap {
  const lastSeen = new Map<string, { value: number | ''; assumed: boolean }>();
  const entriesByKey = new Map<string, MetricHistoryEntry[]>();
  const everRecorded = new Set<string>();

  for (const row of rows) {
    // One malformed Story_JSON__c row is skipped; it must not break the whole history.
    let points: ParsedPoint[];
    try {
      points = parseSnapshotRow(row);
    } catch {
      continue;
    }
    for (const point of points) {
      const key = sliceKey(point.useCaseId, point.key);
      const prev = lastSeen.get(key);
      const changed = !prev || prev.value !== point.value || prev.assumed !== point.assumed;
      if (!changed) continue;
      const shouldEmit = !point.assumed || !!(prev && !prev.assumed);
      lastSeen.set(key, { value: point.value, assumed: point.assumed });
      if (!point.assumed) everRecorded.add(key);
      if (!shouldEmit) continue;
      const list = entriesByKey.get(key) ?? [];
      list.push({ date: point.date, value: point.value, assumed: point.assumed });
      entriesByKey.set(key, list);
    }
  }

  const out: MetricHistoryMap = {};
  for (const [key, entries] of entriesByKey) {
    if (!everRecorded.has(key)) continue; // assumed-only history is just "still default"
    const [useCaseId, metricKey] = splitSliceKey(key);
    (out[useCaseId] ??= {})[metricKey] = entries.slice().reverse(); // newest first
  }
  return out;
}
